// V1 source: customer/midas/components/MidasArtifactSRSummary.tsx
// Strategy: logic-copied
// Verified no-MUI: yes
//
// V2 SR summary viewer — Tier B forward-compat. Bot doesn't actively emit
// sr_summary artifacts today (Round 14: only image / diagram_overlay /
// action_form are active). Verified against the dev-only debug harness
// (`?midas-debug-artifact=sr_summary`).
//
// Logic copied verbatim from V1:
//   - STATUS_COLORS + PRIORITY_COLORS palettes
//   - getStatusColor / getPriorityColor lookup
//   - formatDate / formatDateTime helpers
//   - TimelineItem inner sub-component (dot + connector line)
//   - handleOpenDetail — opens data.link in new tab if it starts with "/"
//
// Visual layer rewritten in Tailwind. Lucide-react icons replace MUI icons.

import React from "react";
import {
  ExternalLink,
  Clock,
  User,
  Tag,
  Cog,
} from "lucide-react";

import { ArtifactTab } from "../../midasPageModels";
import {
  SRSummaryArtifactData,
  SRTimelineEvent,
} from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  Status color map (verbatim from V1)
// ===================================================================

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  open: { bg: "rgba(52,152,219,0.08)", text: "#3498DB", border: "#3498DB" },
  new: { bg: "rgba(52,152,219,0.08)", text: "#3498DB", border: "#3498DB" },
  inprogress: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  in_progress: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  awaitingconfirmation: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  workinprogress: { bg: "rgba(245,158,11,0.08)", text: "#F59E0B", border: "#F59E0B" },
  workcompleted: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
  completed: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
  servicecompleted: { bg: "rgba(39,174,96,0.08)", text: "#27AE60", border: "#27AE60" },
  closed: { bg: "rgba(107,114,128,0.08)", text: "#6B7280", border: "#6B7280" },
  cancelled: { bg: "rgba(192,57,43,0.08)", text: "#C0392B", border: "#C0392B" },
  rejected: { bg: "rgba(192,57,43,0.08)", text: "#C0392B", border: "#C0392B" },
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "#C0392B",
  medium: "#F59E0B",
  low: "#27AE60",
  urgent: "#C0392B",
};

const FALLBACK_STATUS = {
  bg: "rgba(140,131,117,0.08)",
  text: "#8C8375",
  border: "#D8D2C4",
};

const getStatusColor = (status: string) => {
  const key = status.toLowerCase().replace(/[\s_]+/g, "");
  return STATUS_COLORS[key] || FALLBACK_STATUS;
};

const getPriorityColor = (priority: string) => {
  return PRIORITY_COLORS[priority.toLowerCase()] || "#8C8375";
};

// ===================================================================
//  Format helpers (verbatim from V1)
// ===================================================================

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ===================================================================
//  Timeline item
// ===================================================================

const TimelineItem: React.FC<{ event: SRTimelineEvent; isLast: boolean }> = ({
  event,
  isLast,
}) => (
  <div className="relative flex gap-[10px]">
    {/* Dot + line */}
    <div className="flex flex-col items-center pt-[3px]">
      <div className="h-2 w-2 flex-shrink-0 rounded-full bg-mid-blue" />
      {!isLast && <div className="mt-1 w-px flex-1 bg-midas-stone" />}
    </div>
    {/* Content */}
    <div className={`min-w-0 ${isLast ? "" : "pb-[10px]"}`}>
      <div className="font-sora text-[12px] font-medium text-midas-graphite">
        {event.event}
      </div>
      <div className="mt-[2px] flex flex-wrap gap-2">
        <div className="font-mono text-[10px] text-midas-muted-stone">
          {formatDateTime(event.timestamp)}
        </div>
        {event.user && (
          <div className="font-sora text-[10px] text-midas-warm-graphite">
            {event.user}
          </div>
        )}
      </div>
    </div>
  </div>
);

// ===================================================================
//  StatusPill (Tailwind, no MUI dependency)
// ===================================================================

const StatusPill: React.FC<{
  label: string;
  colors: { bg: string; text: string; border: string };
}> = ({ label, colors }) => (
  <span
    className="inline-flex items-center rounded-pill border px-[8px] py-[2px] font-sora text-[11px] font-semibold"
    style={{
      backgroundColor: colors.bg,
      borderColor: colors.border,
      color: colors.text,
    }}
  >
    {label}
  </span>
);

// ===================================================================
//  MidasArtifactSRSummaryV2
// ===================================================================

interface Props {
  tab: ArtifactTab;
}

const MidasArtifactSRSummaryV2: React.FC<Props> = ({ tab }) => {
  const { translate } = useTranslation("chatBot");
  const data = tab.artifact.data as SRSummaryArtifactData | undefined;

  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center font-sora text-[13px] text-midas-muted-stone">
        {translate("midas.artifact.noData")}
      </div>
    );
  }

  const handleOpenDetail = () => {
    const link = data.link;
    if (typeof link === "string" && link.startsWith("/")) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
  };

  const priorityColor = data.priority ? getPriorityColor(data.priority) : null;

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      <div className="flex flex-col gap-[14px] p-4">
        {/* Header — number + status */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="font-mono text-[13px] font-semibold text-midas-warm-graphite">
            {data.number}
          </div>
          <StatusPill
            label={data.status}
            colors={getStatusColor(data.status)}
          />
        </div>

        {/* Title */}
        <div className="font-sora text-[15px] font-bold leading-[1.4] text-midas-graphite">
          {data.title}
        </div>

        {/* Description (truncated to 3 lines) */}
        {data.description && (
          <div
            className="overflow-hidden font-sora text-[12px] leading-[1.5] text-midas-warm-graphite"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {data.description}
          </div>
        )}

        {/* Info pills */}
        <div className="flex flex-wrap gap-2">
          {/* Priority */}
          {data.priority && priorityColor && (
            <span
              className="inline-flex items-center gap-1 rounded-pill border px-[10px] py-1"
              style={{
                backgroundColor: `${priorityColor}12`,
                borderColor: `${priorityColor}30`,
              }}
            >
              <span
                className="h-[6px] w-[6px] rounded-full"
                style={{ backgroundColor: priorityColor }}
              />
              <span
                className="font-sora text-[11px] font-semibold"
                style={{ color: priorityColor }}
              >
                {data.priority}
              </span>
            </span>
          )}

          {/* Created date */}
          <span className="inline-flex items-center gap-1 rounded-pill border border-midas-stone bg-midas-bone-light px-[10px] py-1">
            <Clock size={12} className="text-midas-muted-stone" />
            <span className="font-sora text-[11px] text-midas-warm-graphite">
              {formatDate(data.created_at)}
            </span>
          </span>

          {/* Machine model */}
          {data.machine_model && (
            <span className="inline-flex items-center gap-1 rounded-pill border border-midas-stone bg-midas-bone-light px-[10px] py-1">
              <Cog size={12} className="text-midas-muted-stone" />
              <span className="font-sora text-[11px] text-midas-warm-graphite">
                {data.machine_model}
              </span>
            </span>
          )}

          {/* Offers count */}
          {data.offers_count != null && data.offers_count > 0 && (
            <span className="inline-flex items-center gap-1 rounded-pill border border-midas-stone bg-midas-bone-light px-[10px] py-1">
              <Tag size={12} className="text-midas-muted-stone" />
              <span className="font-sora text-[11px] text-midas-warm-graphite">
                {data.offers_count}{" "}
                {translate("midas.artifact.srSummary.offers")}
              </span>
            </span>
          )}
        </div>

        {/* Assigned provider */}
        {data.assigned_provider && (
          <div
            className="flex items-center gap-2 rounded-md border px-3 py-2"
            style={{
              backgroundColor: "rgba(39,174,96,0.06)",
              borderColor: "rgba(39,174,96,0.12)",
            }}
          >
            <User size={16} style={{ color: "#27AE60" }} />
            <div>
              <div className="font-sora text-[10px] uppercase tracking-[0.3px] text-midas-muted-stone">
                {translate("midas.artifact.srSummary.assignedProvider")}
              </div>
              <div className="font-sora text-[12px] font-medium text-midas-graphite">
                {data.assigned_provider}
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {data.timeline && data.timeline.length > 0 && (
          <div>
            <div className="mb-2 font-sora text-[10px] font-bold uppercase tracking-[0.5px] text-midas-muted-stone">
              {translate("midas.artifact.srSummary.timeline")}
            </div>
            <div className="rounded-md border border-midas-stone bg-midas-bone-light p-3">
              {data.timeline.map((evt, idx) => (
                <TimelineItem
                  key={idx}
                  event={evt}
                  isLast={idx === data.timeline!.length - 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* View Details button */}
        <button
          type="button"
          onClick={handleOpenDetail}
          className="inline-flex items-center justify-center gap-2 rounded-btn bg-midas-terracotta px-4 py-2 font-sora text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#B84A24]"
        >
          {translate("midas.artifact.srSummary.viewDetails")}
          <ExternalLink size={14} />
        </button>
      </div>
    </div>
  );
};

export default MidasArtifactSRSummaryV2;
